import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { unitById } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { useSession } from "../state/session.js";
import { portraitUrl } from "../assets/manifest.js";
import { Stars } from "../units/shared.js";
import { useWorld } from "../world/state.js";
import type { ChatMessage } from "./types.js";
import { EmoteImage } from "../store/cosmeticPresentation.js";

export function jumpToMap(x: number, y: number): void {
  useWorld.getState().setJump({ x, y });
  useWorld.getState().select({ x, y, tile: null });
  navigate("/app/map");
}

/** Renders the parsed segments: mentions highlight, coordinates jump to the map, emotes show their glyph. */
export function MessageBody({ m }: { m: ChatMessage }) {
  const me = useSession((s) => s.account);
  const t = useT();
  if (m.deleted) return <span className="dim">{t("social.chat.deleted")}</span>;
  return (
    <span className="msg-body">
      {m.segments.map((seg, i) => {
        if (seg.kind === "text") return <span key={i}>{seg.text}</span>;
        if (seg.kind === "mention") return <span key={i} className="mention" style={{ color: seg.name.toLowerCase() === me?.name.toLowerCase() ? "var(--gold)" : "var(--accent)" }}>@{seg.name}</span>;
        if (seg.kind === "coords")
          return (
            <button key={i} type="button" className="plain mono coords-link" onClick={() => jumpToMap(seg.x, seg.y)} data-testid="coords-link">
              <FunctionalIcon name="location" /> {seg.x},{seg.y}
            </button>
          );
        return (
          <span key={i} className="emote" title={`:${seg.id}:`}>
            <EmoteImage id={seg.id} />
          </span>
        );
      })}
    </span>
  );
}

/** Attached card: a shared unit, a battle report link or a map coordinate. */
export function CardView({ m }: { m: ChatMessage }) {
  const t = useT();
  const c = m.card;
  if (!c) return null;
  if (c.kind === "unit") {
    const def = unitById(c.unitId);
    return (
      <span className="chat-card">
        <img src={portraitUrl(c.unitId)} alt="" width={34} height={34} />
        <span>
          <strong>{pick(def.name)}</strong>
          <div className="hint">
            Lv {c.level} <Stars stars={c.stars} size={10} />
          </div>
        </span>
      </span>
    );
  }
  if (c.kind === "battle")
    return (
      <button type="button" className="chat-card" onClick={() => navigate(`/app/battle/${c.battleId}`)} data-testid="battle-card">
        <FunctionalIcon name="play" /> {t("social.chat.battleCard")} · {c.won ? t("world.outcome.won") : t("world.outcome.lost")}
      </button>
    );
  return (
    <button type="button" className="chat-card" onClick={() => jumpToMap(c.x, c.y)}>
      <FunctionalIcon name="location" /> {c.label} · {c.x},{c.y}
    </button>
  );
}
